import React, { useEffect, useState } from 'react';
import { Play } from 'lucide-react';
import { TERMS_URL, PRIVACY_URL, openPolicy } from './shared/legal';
import { requestSignup } from './shared/entryIntent';
import ServicesShowcase from './ServicesShowcase';
import AppTour from './AppTour';
import { useLanguage } from '../components/LanguageContext';
import './shared/portals.css';

/**
 * WELCOME — the first thing the app opens on.
 *
 * One photograph, one sentence, one button. The role list that used to sit
 * here asked a question before it had said what the app was for; this screen
 * answers that first and hands straight to the owner sign-in, where the
 * Owner | Tenant switch (RoleSwitch) lets a tenant correct the assumption
 * without coming back.
 *
 * Nothing was deleted. The full role list (Short Stay Guest, Partner, CEO,
 * the services catalogue) is still RoleSelectScreen — but NOTHING HERE LINKS
 * TO IT, on purpose: a visitor should meet one way in, not a menu. Staff reach
 * it with ?roles=all or ?demo=1, which RootRouter reads before it decides
 * which entry screen to render. See entryAsksForRoleList there.
 *
 * The hero is public/showcase-hero.jpg, the golden-hour skyline already
 * generated for the services showcase. It is set from JSX rather than from
 * the stylesheet: CRA's css-loader tries to resolve `url(/foo.jpg)` at build
 * time and fails for assets that live in public/.
 */
interface Props {
  /** Continue into a portal — always 'owner' from here. */
  onStart: () => void;
}

const WelcomeScreen: React.FC<Props> = ({ onStart }) => {
  const { t, language, setLanguage } = useLanguage();
  const [showTour, setShowTour] = useState(false);
  const [showServices, setShowServices] = useState(false);

  // The browser / status bar takes theme-color, which is gold for the app.
  // Over this black screen that read as a gold band, so it goes black here
  // and comes back when the Welcome screen closes.
  useEffect(() => {
    const meta = document.querySelector('meta[name="theme-color"]');
    if (!meta) return;
    const before = meta.getAttribute('content');
    meta.setAttribute('content', '#000000');
    return () => { if (before) meta.setAttribute('content', before); };
  }, []);

  if (showServices) {
    return <ServicesShowcase initialTab="pm" onClose={() => setShowServices(false)} />;
  }
  if (showTour) {
    return (
      <AppTour
        onClose={() => setShowTour(false)}
        onStart={() => { setShowTour(false); onStart(); }}
        onSeePm={() => { setShowTour(false); setShowServices(true); }}
      />
    );
  }

  return (
    <div className="we-screen">
      <div
        className="we-photo"
        aria-hidden="true"
        style={{ backgroundImage: 'url(/showcase-hero.jpg)' }}
      />
      <div className="we-scrim" aria-hidden="true" />

      {/* Fine gold hairline inside the edge — the "printed card" detail. */}
      <div className="we-frame" aria-hidden="true" />

      {/* The AD tile with the name typeset beside it: the wordmark file has
          black type and cannot sit on the photograph. */}
      <div className="we-top">
        <div className="we-brand">
          <img src="/asset-desk-logo.png" alt="Asset Desk" className="we-brand-tile" />
          <span className="we-brand-name" aria-hidden="true">
            <span>ASSET</span>
            <span className="we-brand-gold">DESK</span>
          </span>
        </div>
        <div className="we-lang" role="group" aria-label="Language">
          <button
            className={language === 'en' ? 'we-lang-on' : undefined}
            aria-pressed={language === 'en'}
            onClick={() => setLanguage('en')}
          >EN</button>
          <button
            className={language === 'de' ? 'we-lang-on' : undefined}
            aria-pressed={language === 'de'}
            onClick={() => setLanguage('de')}
          >DE</button>
        </div>
      </div>

      {/* No card: the words sit on the photograph, left-aligned. */}
      <div className="we-body">
        <p className="we-eyebrow">{t('weEyebrow')}</p>
        <h1 className="we-title">
          {t('weHeadlineA')}
          <br />
          <em>{t('weHeadlineB')}</em>
        </h1>

        {/* Sells the result, not the features: the three questions an owner
            actually has, one short line each. */}
        <ul className="we-points">
          <li>{t('wePoint1')}</li>
          <li>{t('wePoint2')}</li>
          <li>{t('wePoint3')}</li>
        </ul>
        <p className="we-sub">{t('weSub')}</p>

        {/* Two named actions instead of one "Get Started" that only ever
            opened the login: most visitors are owners our assistants have
            already registered, so Sign in leads. */}
        <div className="we-actions">
          <button className="we-cta" onClick={onStart}>
            <span>{t('weSignIn')}</span>
            <span className="we-cta-arrow" aria-hidden="true">&rarr;</span>
          </button>
          <button className="we-outline" onClick={() => { requestSignup(); onStart(); }}>
            {t('weCreateAccount')}
          </button>
        </div>

        {/* The film. Large on purpose — many of our owners are 60+, and this
            was a 12px link before. */}
        <button className="we-tour" onClick={() => setShowTour(true)}>
          <span className="we-tour-disc" aria-hidden="true">
            <Play size={20} strokeWidth={2} fill="currentColor" />
          </span>
          <span className="we-tour-text">
            <span className="we-tour-title">{t('weTour')}</span>
            <span className="we-tour-sub">{t('weTourSub')}</span>
          </span>
        </button>

        {/* Reachable before any login, so the policies are readable without an
            account. Deliberately NOT worded as implied consent — explicit,
            versioned consent is captured at sign-up (POLICY_VERSION). */}
        <nav className="we-legal" aria-label={t('rsLegal')}>
          <a href={TERMS_URL} onClick={openPolicy(TERMS_URL)} target="_blank" rel="noopener noreferrer">
            {t('rsTerms')}
          </a>
          <span aria-hidden="true">&middot;</span>
          <a href={PRIVACY_URL} onClick={openPolicy(PRIVACY_URL)} target="_blank" rel="noopener noreferrer">
            {t('rsPrivacy')}
          </a>
        </nav>
      </div>
    </div>
  );
};

export default WelcomeScreen;
